/**
 * Where the two tokens live, and why they live in different places.
 *
 * **Access token: memory only.** It is short-lived, it is presented on nearly every request, and
 * it is the credential worth the least if it survives a reload. Keeping it out of storage means a
 * script that reads storage finds nothing that works right now.
 *
 * **Refresh token: `sessionStorage`.** Something has to survive a page reload or signing in
 * becomes a per-tab ritual, and the refresh token is the smaller thing to persist: one use and the
 * service revokes it (`/auth/refresh` rotates). `sessionStorage` is scoped to the tab and cleared
 * when it closes, so closing the tab ends the session — which is what a person who closed the tab
 * expects.
 *
 * **Not `localStorage`:** it persists until something explicitly clears it, is shared by every tab
 * and every future visit, and turns one XSS into an indefinite session. **Not a cookie we set:**
 * a cookie this app writes is readable by script anyway (it cannot set `HttpOnly`) and is attached
 * automatically to requests, which invents a CSRF surface that the `Authorization` header does not
 * have. An `HttpOnly` cookie would be strictly better than all of this — it is a server decision,
 * and the API does not offer one. That gap is reported rather than papered over.
 */

const REFRESH_KEY = "athena.refresh_token";

/** Never written anywhere. Module-scoped so it dies with the page. */
let accessToken: string | null = null;
/** Epoch milliseconds at which the access token stops being valid. */
let accessTokenExpiresAt = 0;

export interface StoredSession {
  accessToken: string;
  refreshToken: string;
  /** Seconds, as the API reports it. */
  expiresIn: number;
}

function storage(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    // Storage can throw in a hardened or partitioned context. A session that cannot survive a
    // reload is still a usable session; a crash is not.
    return null;
  }
}

export function setSession(session: StoredSession, now: number = Date.now()): void {
  accessToken = session.accessToken;
  accessTokenExpiresAt = now + session.expiresIn * 1000;
  try {
    storage()?.setItem(REFRESH_KEY, session.refreshToken);
  } catch {
    /* see storage() */
  }
}

export function clearSession(): void {
  accessToken = null;
  accessTokenExpiresAt = 0;
  try {
    storage()?.removeItem(REFRESH_KEY);
  } catch {
    /* see storage() */
  }
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function getRefreshToken(): string | null {
  try {
    return storage()?.getItem(REFRESH_KEY) ?? null;
  } catch {
    return null;
  }
}

export function getAccessTokenExpiresAt(): number {
  return accessTokenExpiresAt;
}

/**
 * Seconds of headroom before expiry at which the token is treated as spent.
 *
 * A token that expires in four seconds is a token that expires mid-request. The service's own
 * advice is to refresh on `expires_in` rather than on a 401, and this is what makes that possible.
 */
export const REFRESH_SKEW_SECONDS = 60;

export function isAccessTokenUsable(now: number = Date.now()): boolean {
  return accessToken !== null && accessTokenExpiresAt - REFRESH_SKEW_SECONDS * 1000 > now;
}

/** Test seam. Resets module state that would otherwise leak between cases. */
export function __resetForTests(): void {
  accessToken = null;
  accessTokenExpiresAt = 0;
}
